import type { ReactNode } from "react";
import { cx } from "./cx";

/** Page title block: a "// caption" line over a big display heading, with actions on the right. */
export function PageHeader({ caption, title, actions }: { caption?: ReactNode; title: ReactNode; actions?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div className="flex min-w-0 flex-col gap-1.5">
        {caption && <span className="font-mono text-xs uppercase tracking-widest text-muted">// {caption}</span>}
        {/* The landing page's heading gradient; padding keeps descenders inside the clipped background. */}
        <h1 className="bg-linear-to-r from-black to-[#666666] bg-clip-text pb-1 text-4xl leading-tight font-bold tracking-tighter text-transparent sm:text-5xl">{title}</h1>
      </div>
      {actions && <div className="flex flex-wrap gap-3">{actions}</div>}
    </div>
  );
}

/** Comment-style note under a section, as on turbopuffer. */
export function Note({ children }: { children: ReactNode }) {
  return <p className="font-mono text-xs text-muted">// {children}</p>;
}

/** A number tile. `children` holds an optional visual under the number (a meter, slots, a dial). */
export function Stat({ label, value, sub, children, className }: { label: ReactNode; value: ReactNode; sub?: ReactNode; children?: ReactNode; className?: string }) {
  return (
    <div className={cx("flex flex-col gap-2 rounded-xl border-2 border-ink bg-card p-4.5 shadow-hard-sm", className)}>
      <span className="font-mono text-xs uppercase tracking-widest text-muted">{label}</span>
      <span className="font-display text-3xl font-bold tracking-tight tabular-nums">
        {value} {sub && <span className="text-base font-medium tracking-normal text-muted">{sub}</span>}
      </span>
      {children}
    </div>
  );
}

/**
 * A filled bar for "used of allowed" numbers (storage, stream hours). Turns red near the limit. The label is for
 * screen readers; the numbers next to it are what sighted users read.
 */
export function Meter({ value, max, label, className }: { value: number; max: number; label: string; className?: string }) {
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  return (
    <div role="meter" aria-label={label} aria-valuemin={0} aria-valuemax={max} aria-valuenow={value}
      className={cx("h-3 overflow-hidden rounded-full border-2 border-ink bg-paper", className)}>
      <div
        className={cx("h-full rounded-full motion-safe:transition-[width] motion-safe:duration-1000 motion-safe:ease-out-soft", pct >= 90 ? "bg-accent" : "bg-ink")}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export function Loading({ label = "loading" }: { label?: string }) {
  return (
    <p role="status" className="py-8 font-mono text-muted">
      {label}<span aria-hidden="true" className="motion-safe:animate-pulse">_</span>
    </p>
  );
}

export function Empty({ title, children, action }: { title: ReactNode; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-start gap-3 rounded-xl border-2 border-dashed border-ink bg-card p-8">
      <h2 className="text-xl font-bold">{title}</h2>
      {children && <div className="max-w-prose text-muted">{children}</div>}
      {action}
    </div>
  );
}
